import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Clock, 
  Calculator, 
  AlertTriangle, 
  Building2, 
  Users, 
  Shield,
  FileText,
  Phone,
  CheckCircle2,
  ArrowLeft,
  Zap,
  Volume2,
  Flame,
  Truck,
  HardHat,
  Factory,
  Radiation,
  Wind
} from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";

// טבלת היקף משרה לפי ניקוד סיכון
const scopeTable = [
  { points: "עד 15", hours: 32, days: 4 },
  { points: "16-30", hours: 48, days: 6 },
  { points: "31-45", hours: 64, days: 8 },
  { points: "46-60", hours: 96, days: 12 },
  { points: "60 ומעלה", hours: 180, days: 22 },
];

// גורמי סיכון עיקריים מהנוהל
const riskFactors = [
  { icon: HardHat, name: "פגיעות מכניות", description: "מכונות עיבוד חומרים, מכבשים, גלילים", points: 3 },
  { icon: AlertTriangle, name: "נפילות מגובה", description: "עבודה בגובה מעל 2 מטר", points: 7 },
  { icon: Flame, name: "כוויות תרמיות", description: "חשיפה לחום, אש, משטחים חמים", points: 1 },
  { icon: Radiation, name: "חשיפה כימית כרונית", description: "חומרים מסוכנים, אדים, גזים", points: 7 },
  { icon: Volume2, name: "חשיפה לרעש", description: "רמות רעש מעל המותר", points: 3 },
  { icon: Zap, name: "מערך החשמל", description: "סיכוני חשמל והתחשמלות", points: 4 },
  { icon: Truck, name: "כלי הובלה ממונעים", description: "מלגזות, עגלות, כלי רכב פנימיים", points: 3 },
  { icon: Wind, name: "חשיפה לאבק", description: "אבק תעשייתי, חלקיקים מזיקים", points: 3 },
];

// מאפייני מקום עבודה
const workplaceFactors = [
  { icon: Users, name: "מספר עובדים", description: "49-99: נקודה אחת | 100-249: 2 נקודות | 250-499: 3 נקודות | 500+: 4 נקודות" },
  { icon: AlertTriangle, name: "שיעור תאונות", description: "תאונות מעל הממוצע הארצי: 3 נקודות" },
  { icon: FileText, name: "רישיון עסק", description: "ללא רישיון עסק: 2 נקודות" },
  { icon: Shield, name: "הסמכה לתקן 18001", description: "מפעל מוסמך: הפחתה של 3 נקודות" },
];

// שירותים שאנחנו מציעים
const ourServices = [
  { icon: Calculator, title: "הערכת סיכונים מקצועית", description: "ביצוע סקר סיכונים מקיף לחישוב מדויק של הניקוד הנדרש" },
  { icon: Clock, title: "קביעת היקף משרה", description: "חישוב מדויק של שעות העבודה הנדרשות לממונה הבטיחות" },
  { icon: Users, title: "איתור ממונה מתאים", description: "סיוע במציאת ממונה בטיחות בהיקף המשרה המתאים לצרכים שלכם" },
  { icon: Building2, title: "ליווי מול משרד העבודה", description: "ייצוג וליווי מקצועי מול הרגולטורים" },
];

export default function SafetyOfficerScope() {
  return (
    <Layout>
      <Helmet>
        <title>קביעת היקף משרה לממונה בטיחות | אדם בטיחות</title>
        <meta name="description" content="הנוהל לקביעת היקף משרה של ממונה בטיחות בעבודה. טבלת ניקוד סיכונים, חישוב שעות עבודה חודשיות ומידע מקצועי מלא." />
        <link rel="canonical" href="https://adam-safety.com/services/safety-officer-scope" />
        <meta property="og:title" content="קביעת היקף משרה לממונה בטיחות | אדם בטיחות" />
        <meta property="og:description" content="הנוהל לקביעת היקף משרה של ממונה בטיחות בעבודה. טבלת ניקוד סיכונים, חישוב שעות עבודה חודשיות ומידע מקצועי מלא." />
        <meta property="og:url" content="https://adam-safety.com/services/safety-officer-scope" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="קביעת היקף משרה לממונה בטיחות | אדם בטיחות" />
        <meta name="twitter:description" content="הנוהל לקביעת היקף משרה של ממונה בטיחות בעבודה. טבלת ניקוד סיכונים, חישוב שעות עבודה חודשיות ומידע מקצועי מלא." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/90">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-accent/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              חזרה לשירותים
            </Link>
            
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Clock className="w-5 h-5 text-accent" />
              <span className="text-white/90 text-sm">נוהל משרד העבודה מ-2012</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              קביעת היקף משרה
              <span className="block text-accent mt-2">לממונה בטיחות בעבודה</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              במרץ 2012 חודש הנוהל והוכפל היקף המשרה המינימלי מ-16 ל-32 שעות חודשיות. 
              היקף העבודה נקבע לפי רמות הסיכון אליהן נחשפים העובדים.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-white"
                asChild
              >
                <Link to="/contact">
                  <Phone className="w-5 h-5 ml-2" />
                  שיחת ייעוץ חינם
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                asChild
              >
                <Link to="/services/external-consulting">
                  למידע על ייעוץ חיצוני
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 md:px-8">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="bg-card rounded-2xl p-8 border border-border shadow-lg">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                איך נקבע היקף המשרה?
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                על פי הנוהל, היקף עבודתו של הממונה על הבטיחות תלוי ברמות הסיכון אליהם נחשפים העובדים. 
                על כל חשיפה לסיכון מתקבל ניקוד, כאשר צבירת כלל הניקוד מהווה את סה"כ הניקוד של החברה.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                <strong className="text-foreground">חשוב לדעת:</strong> מפעל עם ניקוד של 0 עד 15 מחויב להעסיק 
                ממונה בטיחות בהיקף משרה מינימלי של <span className="text-accent font-bold">32 שעות עבודה חודשיות</span>.
              </p>
            </div>
          </motion.div>

          {/* Scope Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              טבלת היקף משרה לפי ניקוד סיכון
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full bg-card rounded-2xl overflow-hidden border border-border shadow-lg">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="py-4 px-6 text-right font-bold">סה"כ נקודות סיכון</th>
                    <th className="py-4 px-6 text-center font-bold">שעות עבודה חודשיות</th>
                    <th className="py-4 px-6 text-center font-bold">ימי עבודה בחודש</th>
                  </tr>
                </thead>
                <tbody>
                  {scopeTable.map((row, index) => (
                    <tr 
                      key={index} 
                      className={`border-b border-border last:border-b-0 ${
                        index % 2 === 0 ? 'bg-muted/30' : 'bg-background'
                      } hover:bg-accent/10 transition-colors`}
                    >
                      <td className="py-4 px-6 text-right font-medium text-foreground">{row.points}</td>
                      <td className="py-4 px-6 text-center text-accent font-bold text-lg">{row.hours} שעות</td>
                      <td className="py-4 px-6 text-center text-muted-foreground">{row.days} ימים</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Risk Factors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">
              גורמי סיכון עיקריים
            </h2>
            <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
              על כל גורם סיכון במקום העבודה נצברות נקודות לפי הנוהל
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {riskFactors.map((factor, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-xl p-6 border border-border hover:border-accent/50 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <factor.icon className="w-6 h-6 text-accent" />
                    </div>
                    <span className="bg-primary text-white text-sm font-bold px-3 py-1 rounded-full">
                      {factor.points} נקודות
                    </span>
                  </div>
                  <h3 className="font-bold text-foreground mb-2">{factor.name}</h3>
                  <p className="text-sm text-muted-foreground">{factor.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Workplace Factors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">
              מאפייני מקום העבודה
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              בנוסף לגורמי הסיכון, גם מאפיינים כלליים של מקום העבודה משפיעים על הניקוד
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {workplaceFactors.map((factor, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-xl p-6 border border-border flex gap-4"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <factor.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{factor.name}</h3>
                    <p className="text-sm text-muted-foreground">{factor.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Our Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-primary/5 rounded-3xl p-8 md:p-12 mb-16"
          >
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                איך אדם שייף יכולים לעזור?
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                אנחנו מספקים ליווי מקצועי מלא בכל הקשור לקביעת היקף המשרה של ממונה הבטיחות
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {ourServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-xl p-6 text-center border border-border hover:border-accent hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="font-bold text-foreground mb-2">{service.title}</h3>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="bg-muted/50 rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <FileText className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-foreground mb-2">הבהרה משפטית</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    המידע הכלול בדף זה הינו כללי ואינו מהווה תחליף לקבלת חוות דעת מקצועית. 
                    אף כי נעשה מאמץ למנוע אי דיוקים, ייתכן שנפלו טעויות. 
                    לפיכך השימוש במידע הכלול בדף הינו על אחריותו הבלעדית של המשתמש 
                    ואדם שייף בטיחות בעבודה בע"מ לא תהא אחראית בצורה כלשהי לתוכנו או לשימוש בו.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              צריכים עזרה בחישוב היקף המשרה?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              צוות המומחים שלנו ישמח לבצע עבורכם הערכת סיכונים מקצועית ולקבוע את היקף המשרה המתאים
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-white"
                asChild
              >
                <Link to="/contact">
                  <Phone className="w-5 h-5 ml-2" />
                  קבעו שיחת ייעוץ
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                asChild
              >
                <Link to="/services/safety-officer">
                  <CheckCircle2 className="w-5 h-5 ml-2" />
                  שירותי ממונה בטיחות
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
